import { Checkbox, FormControlLabel, Stack } from "@mui/material";
import { ControlGroup } from "../views/ControlGroup";
import { numberFormatters } from "../../../../shared/utils/numberFormatters";
import { assetService } from "../../../../../shared/services/AssetService";
import { ApplicationTooltip } from "../../../views";
import { NumericField } from "../../../controls";
import dayjs, { Dayjs } from "dayjs";
import { ContractDetailsState } from "./ContractDetails";
import { OrderState } from "../../../../../shared/types/OrderState";
import { oderManagerUtils } from "../utils/OrderManagerUtils";
import { ApplicationDateTimePicker } from "../../../../../shared/components/date-time-picker/ApplicationDateTimePicker";

export type OrderDetailsState = {
    externalId: number;
    state: OrderState;
    isActive: boolean; // obsolete
    archived: boolean; // obsolete
    executedAt: Dayjs,
    underlyingPriceAtExecution: number,
    impliedVolatilityAtExecution: number,
    exchangeOrderId: number | null;
    comment: string | null;
    price: number;
    size: number;
    volume: number;
    delta: number;
    gamma: number;
    vega: number;
    theta: number;
};

type OrderDetailsProps = {
    contractDetails: ContractDetailsState;
    orderDetails: OrderDetailsState;
    handleOrderDetailsChange: (patch: Partial<OrderDetailsState>) => void;
}

export function OrderDetails({
    contractDetails,
    orderDetails,
    handleOrderDetailsChange,
}: OrderDetailsProps) {
    const handleActiveStateChanging = (newValue: boolean): void => {
        handleOrderDetailsChange({
            state: newValue ? OrderState.Active : OrderState.Disabled,            
            isActive: newValue
        });
    }

    const handleArchiveStateChanging = (newValue: boolean): void => {
        handleOrderDetailsChange({
            state: newValue ? OrderState.Archived: OrderState.Active,
            archived: newValue
        });
    }
    
    const handleOpenedAtChange = (newValue: dayjs.Dayjs | null) => {
        if (newValue === null){
            return;
        }

        handleOrderDetailsChange({executedAt: newValue});
    }

    const handleOpenPriceChanging = (newValue: number): void => {
        handleOrderDetailsChange({
            price: newValue,
            volume: -newValue * orderDetails.size,
        });
    }

    const handleSizeChanging = (newSize: number): void => {
        if (assetService.isOptionType(contractDetails.assetType)) {
            handleOrderDetailsChange({
                size: newSize,
                volume: -orderDetails.price * newSize,
                delta: orderDetails.delta * newSize,
                gamma: (orderDetails.gamma ?? 0) * newSize,
                vega: (orderDetails.vega ?? 0) * newSize,
                theta: (orderDetails.theta ?? 0) * newSize,
            });
        }
        else {
            handleOrderDetailsChange({
                size: newSize,
                volume: -orderDetails.price * newSize,
                delta: newSize,
                gamma: 0,
                vega: 0,
                theta: 0,
            });
        }
    }

    const handleUnderlyingPriceAtOpenChange = (newValue: number): void => {
        handleOrderDetailsChange({underlyingPriceAtExecution: newValue});
    }

    const handleImpliedVolatilityAtOpenChange = (newValue: number): void => {
        handleOrderDetailsChange({impliedVolatilityAtExecution: newValue});
    }
    
    return (
        <ControlGroup title="Order details">
            <Stack direction="row" spacing={1}>
                <ApplicationTooltip
                    title="The item is taken into account when calculating portfolio profile"
                    placement={'top-start'}
                    disableInteractive
                    offset={[0, -8]}
                >
                    <FormControlLabel
                        control={
                            <Checkbox
                                sx={{
                                    padding: 0,
                                    marginRight: 0.5,
                                    marginLeft: '-3px',
                                    '&.Mui-disabled': {
                                        color: 'rgba(0, 0, 0, 0.25)',
                                    },
                                }}
                                color="default"
                                id="isActive"
                                name="isActive"
                                checked={orderDetails.state === OrderState.Active || orderDetails.state === OrderState.Archived}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleActiveStateChanging(event.target.checked)}
                                disabled={
                                    orderDetails.archived ||
                                    oderManagerUtils.isOptionCanBeCancelled(orderDetails)
                                }
                            />
                        }
                        label="Active"
                        sx={{
                            padding: 0,
                            margin: 0,
                        }}
                    />
                </ApplicationTooltip>
                <ApplicationTooltip
                    title={
                        orderDetails.isActive
                            ? "If checked, the order has been sent to the exchange and approved"
                            : "Inactive orders can’t be archived"
                    }
                    placement={'top-start'}
                    disableInteractive
                    offset={[0, -8]}
                >
                    <FormControlLabel
                        control={
                            <Checkbox
                                sx={{
                                    padding: 0,
                                    marginRight: 0.5,
                                    '&.Mui-disabled': {
                                        color: 'rgba(0, 0, 0, 0.25)',
                                    },
                                }}
                                color="default"
                                id="archived"
                                name="archived"
                                checked={orderDetails.state === OrderState.Archived}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleArchiveStateChanging(event.target.checked)}
                                disabled={orderDetails.state === OrderState.Disabled}
                            />
                        }
                        label="Archived"
                        sx={{
                            padding: 0,
                            margin: 0
                        }}
                    />
                </ApplicationTooltip>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="flex-end">
                <ApplicationDateTimePicker
                    label="Executed at"
                    value={orderDetails.executedAt}
                    onChange={handleOpenedAtChange}
                    tooltipTitle="Date and time the order was executed"
                    tooltipPlacement={'top-start'}
                    disabled={oderManagerUtils.isOriginalOrderArchived(orderDetails)}
                />
                <ApplicationTooltip
                    title="Implied volatility value at the time the order was executed"
                    placement={'top-start'}
                    disableInteractive
                    offset={[0, -8]}
                >
                    <span>
                        <NumericField
                            id="impliedVolatilityAtOpen"
                            name="impliedVolatilityAtOpen"
                            label="IV"
                            value={orderDetails.impliedVolatilityAtExecution}
                            onValueChange={handleImpliedVolatilityAtOpenChange}
                            formatter={(value: number) => numberFormatters.format4(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                            disabled={!assetService.isOptionType(
                                    contractDetails.assetType) ||
                                    oderManagerUtils.isOriginalOrderArchived(orderDetails)}
                            width={70}
                            step={0.0001}
                        />
                    </span>
                </ApplicationTooltip>
                <ApplicationTooltip
                    title="Underlying asset price at the time the order was executed"
                    placement={'top-start'}
                    disableInteractive
                    offset={[0, -8]}
                >
                    <span>
                        <NumericField
                            id="underlyingPriceAtOpen"
                            name="underlyingPriceAtOpen"
                            label="Underlying price"
                            value={orderDetails.underlyingPriceAtExecution}
                            onValueChange={handleUnderlyingPriceAtOpenChange}
                            formatter={(value: number) => numberFormatters.format0(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                            disabled={!assetService.isOptionType(
                                    contractDetails.assetType) ||
                                    oderManagerUtils.isOriginalOrderArchived(orderDetails)}
                        />
                    </span>
                </ApplicationTooltip>
            </Stack>
            <Stack direction="row" spacing={2}>
                <NumericField
                    id="openPrice"
                    name="openPrice"
                    label="Open price"
                    value={orderDetails.price}
                    onValueChange={handleOpenPriceChanging}
                    formatter={(value: number) => numberFormatters.format2(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                    disabled={oderManagerUtils.isOriginalOrderArchived(orderDetails)}
                />
                <NumericField
                    id="size"
                    name="size"
                    label="Size"
                    value={orderDetails.size}
                    onValueChange={handleSizeChanging}
                    formatter={(value: number) => numberFormatters.format3(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                    width={70}
                    step={0.001}
                    disabled={oderManagerUtils.isOriginalOrderArchived(orderDetails)}
                />
            </Stack>
            <Stack direction="row" spacing={2}>
                <NumericField
                    id="volume"
                    name="volume"
                    label="Volume"
                    value={orderDetails.volume}
                    formatter={(value: number) => numberFormatters.format0(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                    width={70}
                    disabled
                />
                <NumericField
                    id="orderDelta"
                    name="orderDelta"
                    label="Delta (O)"
                    value={orderDetails.delta}
                    formatter={(value: number) => numberFormatters.format2(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                    width={70}
                    disabled
                />
                <NumericField
                    id="orderGamma"
                    name="orderGamma"
                    label="Gamma (O)"
                    value={orderDetails.gamma}
                    formatter={(value: number) => numberFormatters.format6(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                    width={85}
                    disabled
                />
                <NumericField
                    id="orderVega"
                    name="orderVega"
                    label="Vega (O)"
                    value={orderDetails.vega}
                    formatter={(value: number) => numberFormatters.format2(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                    width={65}
                    disabled
                />
                <NumericField
                    id="orderTheta"
                    name="orderTheta"
                    label="Theta (O)"
                    value={orderDetails.theta}
                    formatter={(value: number) => numberFormatters.format2(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                    width={70}
                    disabled
                />
            </Stack>
        </ControlGroup>
    )
}
